import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  useTheme,
  CircularProgress,
  Switch,
  Paper,
  Collapse,
  FormHelperText,
  Avatar,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import NoticeBackgroundPicker from "./NoticeBackgroundPicker";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { alpha } from "@mui/material/styles";
import PublishScheduleOptions from "../scheduling/PublishScheduleOptions";
import SaveToDriveFolderOptions from "../scheduling/SaveToDriveFolderOptions";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { useAccessibleCircles } from "../../hooks/useAccessibleCircles";
import {
  listNoticeAudienceOptions,
  noticeAudiencePickPatch,
  settleNoticeAudience,
  writeNoticeAudiencePatch,
} from "../../domain/notices/noticeAudience";
import { formText } from "./FormText";
import PlaceholderEditor from "./PlaceholderEditor";
import MobileNoticeAttachmentSlots from "./MobileNoticeAttachmentSlots";
import { getAllBackgroundOptions } from "../../config/assets";
import { NoticeboardButton } from "../shared";
import { useSnackbar } from "../../context/SnackbarContext";
import { noticeBodyVisibleLength } from "../../utils/noticeBodyHtml";
import { messageProblem, titleProblem } from "../../domain/notices/noticeRules";

/** Single calm entrance — matches MobileAttachmentComposer. */
const COMPOSER_ENTER = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.52, ease: [0.22, 1, 0.36, 1] as const },
};

/** Matches `PublishScheduleOptions` section headings. */
const sectionHeadingSx = {
  fontWeight: 400,
  color: "primary.main",
  fontSize: { xs: "1.125rem", sm: "1.25rem" },
};

type UploadedFile = {
  fileUrl: string;
  fileName: string;
  contentType: string;
  size: number;
};

type ComposerErrors = {
  title?: string;
  body?: string;
  audience?: string;
  targetSubcircle?: string;
  attachmentUrl?: string;
  publishAt?: string;
};

function validateComposer(
  formData: Record<string, unknown>,
): ComposerErrors {
  const e: ComposerErrors = {};
  const title = titleProblem((formData.title as string) || "");
  if (title) e.title = title;
  const body = messageProblem(((formData.body as string) || "").trim());
  if (body) e.body = body;

  if (!formData.audience)
    e.audience = "Please select who should receive this notice";
  if (
    formData.audience === "subcircle" &&
    !(formData.targetSubcircle as string)?.trim()
  ) {
    e.targetSubcircle = "Please select a target subcircle";
  }

  if (formData.publishMode === "schedule") {
    const publishAt = (formData.publishAt as string) || "";
    if (!publishAt.trim()) {
      e.publishAt = "Choose a publish date and time";
    } else {
      const publishMs = new Date(publishAt).getTime();
      if (Number.isNaN(publishMs) || publishMs <= Date.now() + 60_000) {
        e.publishAt = "Scheduled time must be at least 1 minute in the future";
      }
    }
  }

  return e;
}

interface MobileInfoNoticeComposerProps {
  formData: {
    title: string;
    body: string;
    type: string;
    audience?: string;
    targetSubcircle?: string;
    isUrgent?: boolean;
    eventBackgroundImage?: string;
    attachmentUrl: string;
    uploadedFiles?: UploadedFile[];
    publishMode?: "now" | "schedule";
    publishAt?: string;
    driveFolderKey?: string;
  };
  errors: ComposerErrors;
  isUploading?: boolean;
  uploadStatusMessage?: string;
  uploadProgress?: import("../../utils/presignedUpload").NoticeUploadProgressState | null;
  onCancelUpload?: () => void;
  isSubmitting: boolean;
  handleChange: (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      | { target: { name: string; value: unknown } },
  ) => void;
  applyFormPatch?: (patch: Record<string, unknown>) => void;
  handleFileUpload?: (files: FileList) => Promise<void>;
  handleFileRemove?: (index: number) => void;
  handleSubmit: (
    e: React.FormEvent,
    skipQualityCheck?: boolean,
    forceSubmit?: boolean,
  ) => Promise<void>;
}

const MobileInfoNoticeComposer: React.FC<MobileInfoNoticeComposerProps> = ({
  formData,
  errors: parentErrors,
  isUploading,
  uploadStatusMessage,
  uploadProgress,
  onCancelUpload,
  isSubmitting,
  handleChange,
  applyFormPatch,
  handleFileUpload,
  handleFileRemove,
  handleSubmit,
}) => {
  const theme = useTheme();
  const { user, token } = useAuth();
  const { showMessage } = useSnackbar();
  const schoolName = (user?.schoolName as string) || "";
  const { subcircles, canAccessMainCircle, isLoading: circlesLoading } =
    useAccessibleCircles();

  const audienceCatalog = useMemo(
    () => ({
      organizationName: user?.schoolName,
      organizationLogo: user?.schoolLogo,
      canChooseEveryone: canAccessMainCircle,
      circles: subcircles,
    }),
    [canAccessMainCircle, subcircles, user?.schoolLogo, user?.schoolName],
  );

  const [localErrors, setLocalErrors] = useState<ComposerErrors>({});
  const errors = useMemo(() => ({ ...parentErrors, ...localErrors }), [parentErrors, localErrors]);

  const [moreOpen, setMoreOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [novaOriginal, setNovaOriginal] = useState(formData.body);
  const [novaDirty, setNovaDirty] = useState(false);

  const backgroundOptions = useMemo(() => getAllBackgroundOptions(user?.schoolType), [user?.schoolType]);
  const selectedBackground = formData.eventBackgroundImage || "";

  React.useEffect(() => {
    const settled = settleNoticeAudience("mobileInfo", audienceCatalog, {
      audience: formData.audience,
      targetSubcircle: formData.targetSubcircle,
    });
    if (settled) {
      writeNoticeAudiencePatch(settled, applyFormPatch, handleChange);
    }
  }, [
    applyFormPatch,
    audienceCatalog,
    formData.audience,
    formData.targetSubcircle,
    handleChange,
  ]);

  const audienceCards = useMemo(
    () =>
      listNoticeAudienceOptions({
        catalog: audienceCatalog,
        wire: {
          audience: formData.audience,
          targetSubcircle: formData.targetSubcircle,
        },
        includeInternalTeam: false,
        circleSubtitle: "",
      }),
    [audienceCatalog, formData.audience, formData.targetSubcircle],
  );

  const handleAudienceSelect = (id: string) => {
    setLocalErrors((prev) => {
      if (!prev.audience && !prev.targetSubcircle) return prev;
      const next = { ...prev };
      delete next.audience;
      delete next.targetSubcircle;
      return next;
    });
    writeNoticeAudiencePatch(
      noticeAudiencePickPatch(id),
      applyFormPatch,
      handleChange,
    );
  };

  const handleBackgroundPick = (value: string) => {
    handleChange({ target: { name: "eventBackgroundImage", value } });
  };

  const handleImproveNova = async () => {
    setAiLoading(true);
    try {
      const { generateNoticeContent } = await import("../../utils/aiUtils");
      const ctx = formData.body.trim()
        ? `Title: ${formData.title}\nCurrent Content: ${formData.body}`
        : `Title: ${formData.title || "Notice"}`;
      const result = await generateNoticeContent(
        formData.title || "Notice",
        ctx,
        token,
        schoolName,
      );
      if (result.success && result.text) {
        setNovaOriginal(formData.body || "");
        handleChange({
          target: { name: "body", value: result.text },
        });
        setNovaDirty(true);
      } else {
        const msg = result.error || "";
        const isServerOrGateway =
          /\b502\b|Bad Gateway|503|504|temporarily unavailable/i.test(msg);
        showMessage({
          severity: "error",
          message: isServerOrGateway
            ? "Nova couldn’t reach the AI service. Try again in a moment, or contact support if this continues."
            : msg || "Couldn’t improve your notice. Please try again.",
        });
      }
    } catch (error) {
      console.error(error);
      showMessage({
        severity: "error",
        message: "Couldn’t improve your notice. Please try again.",
      });
    } finally {
      setAiLoading(false);
    }
  };

  const handleNovaRevert = () => {
    handleChange({ target: { name: "body", value: novaOriginal } });
    setNovaDirty(false);
  };

  const onSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validateComposer(formData as Record<string, unknown>);
    setLocalErrors(v);
    if (Object.keys(v).length > 0) return;
    setLocalErrors({});
    await handleSubmit(e, false, true);
  };

  const titleLen = formData.title?.length ?? 0;
  const bodyLen = noticeBodyVisibleLength(formData.body || "");

  return (
    <Box
      component="form"
      onSubmit={onSubmitForm}
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: "100%",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        component={motion.div}
        initial={COMPOSER_ENTER.initial}
        animate={COMPOSER_ENTER.animate}
        transition={COMPOSER_ENTER.transition}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            pr: { xs: 9, sm: 9 },
            flexShrink: 0,
            pt: {
              xs: "calc(env(safe-area-inset-top, 0px) + 52px)",
              md: "calc(env(safe-area-inset-top, 0px) + 52px)",
            },
            pb: 1,
          }}
        >
          <Typography
            variant="h5"
            sx={{ color: "primary.main", fontWeight: 600, fontSize: "1.25rem" }}
          >
            Create Notice
          </Typography>
        </Box>

        <Divider />

        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 2,
            overflowY: "auto",
            flex: 1,
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="h6" sx={sectionHeadingSx}>
              Title
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {titleLen}/100
            </Typography>
          </Box>
          <PlaceholderEditor
            value={formData.title || ""}
            onChange={(value) =>
              handleChange({ target: { name: "title", value } })
            }
            placeholder="Add a short title for your notice..."
            error={!!errors.title}
            helperText={errors.title}
            inputProps={{ maxLength: 100 }}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
            <Typography variant="h6" sx={sectionHeadingSx}>
              {formText.labels.body}
            </Typography>
            <Button
              type="button"
              size="small"
              variant="text"
              onClick={handleImproveNova}
              disabled={aiLoading}
              sx={{ textTransform: "none", fontWeight: 600, minWidth: 0 }}
              startIcon={
                aiLoading ? (
                  <CircularProgress size={14} />
                ) : (
                  <AutoAwesomeIcon sx={{ fontSize: 18, color: "primary.main" }} />
                )
              }
            >
              Improve with Nova
            </Button>
          </Box>
          {novaDirty && (
            <Button
              type="button"
              size="small"
              onClick={handleNovaRevert}
              sx={{ mb: 1, textTransform: "none" }}
            >
              Revert
            </Button>
          )}
          <Box sx={{ position: "relative", mb: 2 }}>
            <PlaceholderEditor
              value={formData.body || ""}
              onChange={(value) =>
                handleChange({ target: { name: "body", value } })
              }
              placeholder={formText.placeholders.body}
              multiline
              minRows={5}
              maxRows={12}
              error={!!errors.body}
              helperText={errors.body}
              inputProps={{ maxLength: 2000 }}
              sx={{ "& .MuiOutlinedInput-root": { fontSize: "1rem" } }}
            />
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                right: 10,
                bottom: errors.body ? 28 : 8,
                color: "text.secondary",
              }}
            >
              {bodyLen}/1000
            </Typography>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 1.25,
              borderRadius: 2,
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <NotificationsActiveOutlinedIcon
              sx={{ color: "error.main", fontSize: 26 }}
            />
            <Typography sx={{ flex: 1, fontWeight: 500, fontSize: "0.95rem" }}>
              Mark as urgent notice
            </Typography>
            <Switch
              checked={!!formData.isUrgent}
              onChange={(_, checked) =>
                handleChange({
                  target: { name: "isUrgent", value: checked },
                })
              }
              color="primary"
            />
          </Paper>

          <Box sx={{ mb: 2 }}>
            <NoticeBackgroundPicker options={backgroundOptions} value={selectedBackground} onChange={handleBackgroundPick} noticeText={`${formData.title || ""} ${formData.body || ""}`} />
          </Box>

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              mb: 2,
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => setMoreOpen((o) => !o)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ")
                  setMoreOpen((o) => !o);
              }}
              sx={{
                p: 1.35,
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <AutoAwesomeIcon sx={{ color: "#ca8a04", fontSize: 22 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }}>
                  More options (optional)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Add photos, documents, or videos (up to 20)
                </Typography>
              </Box>
              {moreOpen ? (
                <ExpandMoreIcon sx={{ color: "text.secondary", transform: "rotate(180deg)" }} />
              ) : (
                <ChevronRightIcon sx={{ color: "text.secondary" }} />
              )}
            </Box>
            <Collapse in={moreOpen}>
              <Box sx={{ px: 1.35, pb: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <MobileNoticeAttachmentSlots
                  embedded
                  mode="attachment"
                  uploadedFiles={formData.uploadedFiles}
                  errors={errors}
                  isUploading={isUploading}
                  uploadStatusMessage={uploadStatusMessage}
                  uploadProgress={uploadProgress}
                  onCancelUpload={onCancelUpload}
                  handleFileUpload={handleFileUpload}
                  handleFileRemove={handleFileRemove}
                />
              </Box>
            </Collapse>
          </Paper>

          <Box sx={{ mb: 2 }}>
            <PublishScheduleOptions
              publishMode={formData.publishMode || "now"}
              publishAt={formData.publishAt || ""}
              onPublishModeChange={(mode) =>
                handleChange({ target: { name: "publishMode", value: mode } })
              }
              onPublishAtChange={(value) =>
                handleChange({ target: { name: "publishAt", value } })
              }
            />
            {errors.publishAt && (
              <FormHelperText error sx={{ mt: 0.5 }}>
                {errors.publishAt}
              </FormHelperText>
            )}
          </Box>

          <Box sx={{ mb: 10 }}>
            <Typography variant="h6" sx={{ ...sectionHeadingSx, mb: 1 }}>
              Choose who this notice is sent to.
            </Typography>
            {circlesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Stack direction="row" spacing={1.25} sx={{ overflowX: "auto", pb: 1.5, scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
                {audienceCards.map((opt) => (
                  <Box
                    key={opt.id}
                    onClick={() => handleAudienceSelect(opt.id)}
                    sx={{
                      cursor: "pointer",
                      flexShrink: 0,
                      textAlign: "center",
                      minWidth: 88,
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: "50%",
                        p: opt.selected ? "2px" : 0,
                        border: opt.selected ? "2px solid" : "2px solid transparent",
                        borderColor: opt.selected ? "primary.main" : "transparent",
                        display: "inline-block",
                        position: "relative",
                      }}
                    >
                      <Avatar
                        src={opt.avatarSrc}
                        alt={opt.label}
                        sx={{
                          width: 64,
                          height: 64,
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          color: "primary.main",
                          fontWeight: 700,
                        }}
                      >
                        {opt.avatarText}
                      </Avatar>
                      {opt.selected && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            bgcolor: "primary.main",
                            color: "common.white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "2px solid",
                            borderColor: "common.white",
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 13 }} />
                        </Box>
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 0.65, px: 0.25, lineHeight: 1.2 }}
                    >
                      {opt.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
            {(errors.audience || errors.targetSubcircle) &&
              !audienceCards.some((c) => c.selected) && (
              <FormHelperText error sx={{ mt: -0.5, mb: 1.5 }}>
                {errors.audience || errors.targetSubcircle}
              </FormHelperText>
            )}
            {(Boolean(formData.attachmentUrl?.trim()) ||
              Boolean(formData.uploadedFiles && formData.uploadedFiles.length > 0)) && (
              <SaveToDriveFolderOptions
                audience={formData.audience || ""}
                targetSubcircle={formData.targetSubcircle}
                audienceLabel={
                  formData.audience === "circle"
                    ? schoolName || "Entire Circle"
                    : subcircles.find(
                        (s) => s.circleId === formData.targetSubcircle,
                      )?.name || ""
                }
                driveFolderKey={formData.driveFolderKey || ""}
                publishMode={formData.publishMode || "now"}
                onDriveFolderKeyChange={(value) =>
                  handleChange({ target: { name: "driveFolderKey", value } })
                }
              />
            )}
          </Box>

        </Box>

        <Box
          sx={{
            flexShrink: 0,
            px: 2,
            py: 1.5,
            pb: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <NoticeboardButton
            type="submit"
            variant="primary"
            disabled={isSubmitting || !!isUploading}
            sx={{ width: "100%", py: 1.25, fontSize: "1rem", fontWeight: 600 }}
            startIcon={
              formData.publishMode === "schedule" ? (
                <ScheduleIcon sx={{ fontSize: 20 }} />
              ) : undefined
            }
          >
            {formData.publishMode === "schedule" ? "Schedule Notice" : "Post Notice"}
          </NoticeboardButton>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            justifyContent="center"
            sx={{ mt: 1 }}
          >
            <LockOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              Review your notice details before posting
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default MobileInfoNoticeComposer;
